import { NETWORK_PLUGIN, type NetworkFailure, type NetworkRequest } from '@belvdr/shared'
import { bodyRecorder, isTextual, type MaskNetworkBody } from './network-body.js'
import { type MaskNetworkUrl, urlRecorder } from './network-url.js'

/** Corps bruts d'une requête en erreur, quand ils sont textuels (`null` sinon). */
type RawBodies = { request: string | null; response: string | null }

/**
 * Requête terminée, avec son URL telle que la page l'a demandée (relative, brute), et ses corps
 * bruts si elle est en erreur et que leur capture est demandée.
 */
type Emit = (request: NetworkRequest, bodies?: RawBodies) => void

/** Les corps des réponses de ce statut sont-ils à capturer (`captureErrorBodies`) ? */
const hasErrorStatus = (status: number) => status >= 400

/** Fenêtre enregistrée, avec ses constructeurs (`fetch`, `XMLHttpRequest`…). */
type Page = Window & typeof globalThis

/** Taille annoncée par la réponse (`Content-Length`), si elle est connue. */
function contentLength(value: string | null): number | undefined {
  if (value === null) return undefined
  const size = Number(value)
  return Number.isInteger(size) && size >= 0 ? size : undefined
}

/** Échec d'un `fetch` d'après son erreur : annulation (`AbortController`), délai, ou réseau. */
function failureOf(error: unknown): NetworkFailure {
  const name = (error as { name?: unknown } | null)?.name
  if (name === 'AbortError') return 'aborted'
  if (name === 'TimeoutError') return 'timeout'
  return 'network'
}

/** Texte d'un corps envoyé (`body` de `fetch` ou de `XMLHttpRequest.send`), s'il est textuel. */
function sentText(body: unknown): string | null {
  if (typeof body === 'string') return body
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
    return body.toString()
  // FormData, Blob, ArrayBuffer, flux, Document… : non textuels ou illisibles sans les consommer.
  return null
}

/**
 * Corps envoyé par un `fetch`, s'il est textuel : celui de `init`, sinon celui de la `Request`
 * (lu sur une copie, avant que l'envoi ne le consomme).
 */
function fetchRequestText(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Promise<string | null> {
  if (init?.body !== undefined && init.body !== null) return Promise.resolve(sentText(init.body))
  const isRequest = typeof Request !== 'undefined' && input instanceof Request
  if (!isRequest || input.body === null || !isTextual(input.headers.get('Content-Type')))
    return Promise.resolve(null)
  try {
    return input
      .clone()
      .text()
      .catch(() => null)
  } catch {
    // Corps déjà consommé : la page recevra l'erreur de `fetch`.
    return Promise.resolve(null)
  }
}

/** Corps d'une réponse, s'il est textuel (lu sur une copie : la page lit la réponse intacte). */
function responseText(response: Response): Promise<string | null> {
  if (!isTextual(response.headers.get('Content-Type'))) return Promise.resolve(null)
  try {
    return response
      .clone()
      .text()
      .catch(() => null)
  } catch {
    return Promise.resolve(null)
  }
}

/** Remplace `win.fetch` par une version qui enregistre chaque requête ; renvoie le rétablissement. */
function observeFetch(win: Page, emit: Emit, captureBodies: boolean): () => void {
  const original = win.fetch
  let active = true
  function recordedFetch(this: unknown, input: RequestInfo | URL, init?: RequestInit) {
    if (!active) return original.call(this, input, init)
    const startedAt = Date.now()
    const isRequest = typeof Request !== 'undefined' && input instanceof Request
    const url = isRequest ? input.url : String(input)
    const method = (init?.method ?? (isRequest ? input.method : 'GET')).toUpperCase()
    const request = { url, method, type: 'fetch', startedAt }
    const sent = captureBodies ? fetchRequestText(input, init) : null
    const pending = original.call(this, input, init)
    pending.then(
      (response) => {
        const recorded = {
          ...request,
          duration: Date.now() - startedAt,
          status: response.status,
          ...optional('size', contentLength(response.headers.get('Content-Length'))),
        }
        if (!sent || !hasErrorStatus(response.status)) return emit(recorded)
        void Promise.all([sent, responseText(response)]).then(([sentBody, received]) =>
          emit(recorded, { request: sentBody, response: received }),
        )
      },
      (error: unknown) =>
        emit({ ...request, duration: Date.now() - startedAt, failure: failureOf(error) }),
    )
    return pending
  }
  win.fetch = recordedFetch as typeof win.fetch
  return () => {
    // Enveloppée depuis par un autre outil : elle reste en place, inactive.
    active = false
    if (win.fetch === recordedFetch) win.fetch = original
  }
}

/** Corps de la réponse d'un `XMLHttpRequest` terminé, s'il est textuel. */
function xhrResponseText(xhr: XMLHttpRequest): string | null {
  try {
    const responseType = xhr.responseType || 'text'
    if (responseType === 'json') return xhr.response === null ? null : JSON.stringify(xhr.response)
    if (responseType !== 'text' || !isTextual(xhr.getResponseHeader('Content-Type'))) return null
    return xhr.responseText
  } catch {
    return null
  }
}

/**
 * Enregistre les requêtes `XMLHttpRequest` (méthode et URL à `open`, début à `send`, issue à
 * `loadend`) ; renvoie le rétablissement.
 */
function observeXhr(win: Page, emit: Emit, captureBodies: boolean): () => void {
  const prototype = win.XMLHttpRequest.prototype
  const { open, send } = prototype
  const opened = new WeakMap<XMLHttpRequest, { method: string; url: string }>()
  let active = true

  const recordedOpen = function (this: XMLHttpRequest, method: string, url: string | URL) {
    opened.set(this, { method: method.toUpperCase(), url: String(url) })
    // eslint-disable-next-line prefer-rest-params
    return open.apply(this, arguments as unknown as Parameters<typeof open>)
  } as typeof open

  const recordedSend = function (
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const target = opened.get(this)
    if (active && target) {
      const startedAt = Date.now()
      const sent = captureBodies ? sentText(body) : null
      let failure: NetworkFailure | undefined
      const onFailure = (event: Event) => {
        failure =
          event.type === 'abort' ? 'aborted' : event.type === 'timeout' ? 'timeout' : 'network'
      }
      for (const type of ['error', 'abort', 'timeout']) this.addEventListener(type, onFailure)
      this.addEventListener(
        'loadend',
        () => {
          const request = {
            url: target.url,
            method: target.method,
            type: 'xmlhttprequest',
            startedAt,
            duration: Date.now() - startedAt,
          }
          if (failure !== undefined || this.status === 0) {
            emit({ ...request, failure: failure ?? 'network' })
          } else {
            const recorded = {
              ...request,
              status: this.status,
              ...optional('size', contentLength(this.getResponseHeader('Content-Length'))),
            }
            if (!captureBodies || !hasErrorStatus(this.status)) return emit(recorded)
            emit(recorded, { request: sent, response: xhrResponseText(this) })
          }
        },
        { once: true },
      )
    }
    return send.call(this, body)
  }

  prototype.open = recordedOpen
  prototype.send = recordedSend
  return () => {
    // Remplacées depuis par un autre outil : elles restent en place, inactives.
    active = false
    if (prototype.open === recordedOpen) prototype.open = open
    if (prototype.send === recordedSend) prototype.send = send
  }
}

/** Types de ressources déjà enregistrés par `observeFetch` et `observeXhr`. */
const INTERCEPTED_TYPES = new Set(['fetch', 'xmlhttprequest'])

/**
 * Enregistre les ressources statiques (scripts, feuilles de style, images, polices…) chargées
 * pendant l'enregistrement, signalées par le navigateur (Resource Timing). Celles chargées avant
 * `startSession()` ne le sont pas.
 */
function observeResources(win: Page, emit: Emit): () => void {
  if (typeof win.PerformanceObserver !== 'function') return () => {}
  const observer = new win.PerformanceObserver((list) => {
    for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
      if (entry.entryType !== 'resource' || INTERCEPTED_TYPES.has(entry.initiatorType)) continue
      // 0 : statut ou taille non exposés (autre origine sans Timing-Allow-Origin, cache…).
      const status = entry.responseStatus || undefined
      const size = entry.encodedBodySize || undefined
      emit({
        url: entry.name,
        method: 'GET',
        type: entry.initiatorType,
        startedAt: Math.round(win.performance.timeOrigin + entry.startTime),
        duration: Math.round(entry.duration),
        ...optional('status', status),
        ...optional('size', size),
      })
    }
  })
  try {
    observer.observe({ type: 'resource' })
  } catch {
    // Navigateur sans Resource Timing : pas de ressources statiques.
    return () => {}
  }
  return () => observer.disconnect()
}

/** `{ [key]: value }`, ou rien si `value` est absente (le format omet les champs inconnus). */
function optional<K extends string, V>(key: K, value: V | undefined) {
  return (value === undefined ? {} : { [key]: value }) as Partial<Record<K, V>>
}

export type NetworkOptions = {
  /** Origine de l'API belvdr : ses requêtes (envoi des batchs, script du SDK) sont ignorées. */
  apiHost: string
  maskUrl?: MaskNetworkUrl | undefined
  /** Capture des corps des requêtes en erreur (4xx / 5xx), désactivée par défaut. */
  captureErrorBodies?: boolean | undefined
  maskBody?: MaskNetworkBody | undefined
}

/**
 * Plugin d'enregistrement rrweb du réseau : chaque requête terminée devient un événement de
 * plugin (`belvdr/network@1`, cf. `NetworkRequest` de `@belvdr/shared`), son URL nettoyée
 * dans le navigateur. Aucun en-tête n'est enregistré ; les corps seulement pour les réponses
 * 4xx / 5xx si `captureErrorBodies`, textuels, masqués et tronqués (cf. `bodyRecorder`).
 */
export function networkPlugin({
  apiHost,
  maskUrl,
  captureErrorBodies = false,
  maskBody,
}: NetworkOptions) {
  const recordedUrl = urlRecorder({ apiHost, maskUrl })
  const recordedBody = bodyRecorder({ maskBody })
  return {
    name: NETWORK_PLUGIN,
    options: {},
    observer(cb: (payload: unknown) => void, win: Window) {
      // La fenêtre enregistrée (la page, ou une iframe de même origine), avec ses constructeurs.
      const page = win as Page
      const emit: Emit = (request, bodies) => {
        const url = recordedUrl(request.url, page.location.href)
        if (url === null) return
        const body = (kind: 'request' | 'response') => {
          const text = bodies?.[kind]
          if (text == null || request.status === undefined) return undefined
          const context = {
            kind,
            url: new URL(request.url, page.location.href).href,
            method: request.method,
            status: request.status,
          }
          return recordedBody(text, context) ?? undefined
        }
        const requestBody = body('request')
        const responseBody = body('response')
        cb({
          ...request,
          url,
          ...optional('requestBody', requestBody),
          ...optional('responseBody', responseBody),
        })
      }
      const stops = [
        observeFetch(page, emit, captureErrorBodies),
        observeXhr(page, emit, captureErrorBodies),
        observeResources(page, emit),
      ]
      return () => stops.forEach((stop) => stop())
    },
  }
}
